import { FC, ReactNode } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'

import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import styles from './SectionHead.module.sass'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Section title, the heading's content; a decorative part of it (a number disc) takes `aria-hidden` */
  title: ReactNode
  /** Heading element of the title, to fit the page outline (default `h2`) */
  titleComponent?: 'h2' | 'h3' | 'h4'
  /** Id of the heading, e.g. for a section named by it through `aria-labelledby` */
  id?: string
  /** A rule under the row, as section headers are drawn */
  isDivided?: boolean
  /** What sits on the right of the title: a count, a filter, a switch */
  trailing?: ReactNode
  /** A line under the title */
  description?: ReactNode
  /** Additional class on the root, e.g. for the spacing around it */
  className?: string
}

/**
 * @component The heading row of a section: the title (and an optional line under it) on the left, whatever the
 * section offers on the right, optionally with a rule under the row.
 * @example
 * <SectionHead titleComponent="h3" isDivided title={t('schedule.title')} trailing={<MUISwitch label={t('calendar')} />} />
 */
export const SectionHead: FC<Props> = ({
  title,
  titleComponent = 'h2',
  id,
  isDivided = false,
  trailing,
  description,
  className,
}) => (
  <div className={classnames(styles.sectionHead, { [styles.divided]: isDivided }, className)}>

    <div className={styles.titles}>

      <Typography
        id={id}
        variant="text-lg"
        component={titleComponent}
        // `&&` outranks the old-style routes' global h1-h4 sizes and colour (src/styles/index.sass)
        sx={theme => ({ '&&': { margin: 0, fontSize: theme.typography['text-lg']?.fontSize, lineHeight: theme.typography['text-lg']?.lineHeight, fontWeight: 550, color: GRAY_900 } })}
      >
        {title}
      </Typography>

      {description != null && <Typography variant="text-sm" component="p" color={GRAY_700}>{description}</Typography>}

    </div>

    {trailing}

  </div>
)
